import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DrillingData} from "../../../../types/DrillingData";
import {ColumnsType} from "antd/es/table";
import {Button, Input, message, Select, Space, Table} from "antd";
import {fetchSelectOptionsByContractNO} from "../../../../backendInteraction/ApiFetch";
import {
    addDrillingRow, deleteDrillingByDid,
    fetchDrillingByPid, fetchTotalDrillingMetresAndCoreLost,
    updateDrillingRow
} from "../../../../backendInteraction/DrillingApiFetch";
import {depthValueValidator, numberValidator} from "../../../../utils/Validator";

interface DrillingTableProps {
    data: DrillingData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    depth: string;
    setHoleId: (value: string) => void;
    setDepth: (value: string) => void;
    onTotalDrillingMetresUpdate?: (total: number) => void;
    onTotalCoreLostUpdate?: (total: number) => void;
}
const DrillingTable: React.FC<DrillingTableProps> = ({data, plodId, contractNo, isView,
                                                         isAddNewRow, setIsAddNewRow, holeId, depth,
                                                         setHoleId, setDepth, onTotalDrillingMetresUpdate, onTotalCoreLostUpdate}) => {
    const [drillingData, setDrillingData] = useState<DrillingData []>([]);

    const [drillTypeOptions, setDrillTypeOptions] = useState<{value: string; label: string}[]>([]);
    const [drillSizeOptions, setDrillSizeOptions] = useState<{value: string; label: string}[]>([]);
    const [barrelOptions, setBarrelOptions] = useState<{value: string; label: string}[]>([]);

    const [editingDid, setEditingDid] = useState<number | null>(null);
    const [editData, setEditData] = useState<DrillingData | null>(null);


    const loadOptions = useCallback(async () => {
        if (contractNo) {
            const types = await fetchSelectOptionsByContractNO(contractNo, "DrillType");
            const sizes = await fetchSelectOptionsByContractNO(contractNo, "DrillSize");
            const barrels = await fetchSelectOptionsByContractNO(contractNo, "Barrel");
            setDrillTypeOptions(types.map(type => ({ value: type, label: type })));
            setDrillSizeOptions(sizes.map(size => ({ value: size, label: size })));
            setBarrelOptions(barrels.map(barrel => ({ value: barrel, label: barrel })));
        } else {
            setDrillTypeOptions([]);
            setDrillSizeOptions([]);
            setBarrelOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        setDrillingData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingDid !== -1) {
            const len = drillingData.length;
            const newRow: DrillingData = {
                key: -1,
                did: -1,
                hole_id: len > 0 ? drillingData[len - 1].hole_id || holeId : holeId,
                angle: "",
                drill_type: "",
                size: "",
                depth_from: len > 0 ? drillingData[len - 1].depth_to || depth : depth,
                depth_to: "",
                total_m: "",
                barrel: "",
                recovered_metres: "",
                dci_metres: "",
                non_chargeable_metres: "",
                pid: plodId ?? -1,
            };
            setDrillingData(prevData => {
                const hasNewRow = prevData.some(item => item.did === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingDid(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingDid]);

    useEffect(() => {
        if (isView) {
            if (editingDid === -1) {
                setDrillingData(prevData => prevData.filter(item => item.did !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingDid(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { angle, depth_from, depth_to, recovered_metres, dci_metres, non_chargeable_metres, hole_id } = editData;

        if(!!angle) {
            const isNumber = numberValidator(angle, 'Angle');
            if (!isNumber) return;
            const angleVal = parseFloat(angle);
            if (angleVal < -360 || angleVal > 360) {
                message.error('The "Angle" value must be between -360 degree and 360 degree.');
                return;
            }
        }

        if(!depthValueValidator(depth_from, depth_to, false)) return;

        if(!!recovered_metres) {
            const isNumber = numberValidator(recovered_metres, 'Recovered Metres');
            if (!isNumber) return;
            const recov_m = parseFloat(recovered_metres);
            if (recov_m < 0) {
                message.error('The "Recovered Metres" value cannot be less than 0.');
                return;
            }
        }

        if(!!dci_metres) {
            const isNumber = numberValidator(dci_metres, 'DCI Metres');
            if (!isNumber) return;
            const dci_m = parseFloat(dci_metres);
            if (dci_m < 0) {
                message.error('The "DCI Metres" value cannot be less than 0.');
                return;
            }
        }

        if(!!non_chargeable_metres) {
            const isNumber = numberValidator(non_chargeable_metres, 'N/C Metres');
            if (!isNumber) return;
            const nc_m = parseFloat(non_chargeable_metres);
            if (nc_m < 0) {
                message.error('The "N/C Metres" value cannot be less than 0.');
                return;
            }
        }

        const updatedEditData = { ...editData, total_m: !!depth_from&&!!depth_to ? parseFloat(depth_to) - parseFloat(depth_from) + '' : ''};
        setEditData(updatedEditData);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingDid === -1) {
            const success = await addDrillingRow(updatedEditData, aid);
            if (success) {
                setDrillingData(await fetchDrillingByPid(plodId ?? -1, aid));
                message.success("New drilling added successfully!");

                if (onTotalDrillingMetresUpdate && onTotalCoreLostUpdate) {
                    const { totalMetres, totalCoreLost } = await fetchTotalDrillingMetresAndCoreLost(plodId, aid);
                    onTotalDrillingMetresUpdate(totalMetres);
                    onTotalCoreLostUpdate(totalCoreLost);
                }

            } else {
                message.error("Failed to add new drilling.");
            }
        } else {
            const success = await updateDrillingRow(editingDid ?? -1, updatedEditData);
            if (success) {
                setDrillingData(await fetchDrillingByPid(plodId ?? -1, aid));
                message.success("Drilling updated successfully!");

                if (onTotalDrillingMetresUpdate && onTotalCoreLostUpdate) {
                    const { totalMetres, totalCoreLost } = await fetchTotalDrillingMetresAndCoreLost(plodId, aid);
                    onTotalDrillingMetresUpdate(totalMetres);
                    onTotalCoreLostUpdate(totalCoreLost);
                }

            } else {
                message.error("Failed to update drilling.");
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);
        !(depth_to === depth) && setDepth(depth_to);

        if (editingDid === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingDid(null);
        setEditData(null);
    }, [editData, editingDid, setIsAddNewRow]);

    const cancelEditing = useCallback((record: DrillingData) => {
        console.log("Cancel editing");
        setEditingDid(null);
        setEditData(null);

        if (record.did === -1) {
            setDrillingData(prevData => prevData.filter(item => item.did !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: DrillingData) => {
        console.log("Editing row:", record);

        if (editingDid === -1) {
            setDrillingData(prevData => prevData.filter(item => item.did !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingDid(prevId => {
            if (prevId !== null) cancelEditing({ did: prevId } as DrillingData);
            return record.did;
        });

        setEditData({ ...record });
    }, [editingDid, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: DrillingData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteDrillingByDid(record.did);
        if (success) {
            setDrillingData(await fetchDrillingByPid(plodId ?? -1, aid));
            message.success("Drilling deleted successfully!");

            if (onTotalDrillingMetresUpdate && onTotalCoreLostUpdate) {
                const { totalMetres, totalCoreLost } = await fetchTotalDrillingMetresAndCoreLost(plodId, aid);
                onTotalDrillingMetresUpdate(totalMetres);
                onTotalCoreLostUpdate(totalCoreLost);
            }

        } else {
            message.error("Failed to delete drilling.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof DrillingData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<DrillingData> = useMemo(() => [
        {
            title: 'Hole ID',
            dataIndex: 'hole_id',
            key: 'hole_id',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: 'Angle',
            dataIndex: 'angle',
            key: 'angle',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Input
                        value={editData?.angle}
                        onChange={(e) => handleInputChange("angle", e.target.value)}
                    />
                ) : (
                    record.angle
                ),
        },
        {
            title: 'Drill Type',
            dataIndex: 'drill_type',
            key: 'drill_type',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={drillTypeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.drill_type || ""}
                        onChange={(value) => handleInputChange("drill_type", value || "")}
                    />
                ) : (
                    record.drill_type
                ),
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={drillSizeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.size || ""}
                        onChange={(value) => handleInputChange("size", value || "")}
                    />
                ) : (
                    record.size
                ),
        },
        {
            title: 'From',
            dataIndex: 'depth_from',
            key: 'depth_from',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Input
                        value={editData?.depth_from}
                        onChange={(e) => handleInputChange("depth_from", e.target.value)}
                    />
                ) : (
                    record.depth_from
                ),
        },
        {
            title: 'To',
            dataIndex: 'depth_to',
            key: 'depth_to',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Input
                        value={editData?.depth_to}
                        onChange={(e) => handleInputChange("depth_to", e.target.value)}
                    />
                ) : (
                    record.depth_to
                ),
        },
        {
            title: 'Barrel',
            dataIndex: 'barrel',
            key: 'barrel',
            width: "16%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={barrelOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.barrel || ""}
                        onChange={(value) => handleInputChange("barrel", value || "")}
                    />
                ) : (
                    record.barrel
                ),
        },
        {
            title: 'Recovered(m)',
            dataIndex: 'recovered_metres',
            key: 'recovered_metres',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Input
                        value={editData?.recovered_metres}
                        onChange={(e) => handleInputChange("recovered_metres", e.target.value)}
                    />
                ) : (
                    record.recovered_metres
                ),
        },
        {
            title: 'DCI(m)',
            dataIndex: 'dci_metres',
            key: 'dci_metres',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Input
                        value={editData?.dci_metres}
                        onChange={(e) => handleInputChange("dci_metres", e.target.value)}
                    />
                ) : (
                    record.dci_metres
                ),
        },
        {
            title: 'N/C(m)',
            dataIndex: 'non_chargeable_metres',
            key: 'non_chargeable_metres',
            width: "8%",
            render: (_, record) =>
                editingDid === record.did ? (
                    <Input
                        value={editData?.non_chargeable_metres}
                        onChange={(e) => handleInputChange("non_chargeable_metres", e.target.value)}
                    />
                ) : (
                    record.non_chargeable_metres
                ),
        },
        ...(!isView
            ? [{
                title: 'Action',
                key: 'action',
                width: "12%",
                render: (_, record) => (
                    <Space size="small">
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingDid === record.did ? saveChanges() : startEditing(record))}>
                            {editingDid === record.did ? "Save" : "Edit"}
                        </Button>
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingDid === record.did ? cancelEditing(record) : deleteRow(record))}>
                            {editingDid === record.did ? "Cancel" : "Delete"}
                        </Button>
                    </Space>
                ),
            },
        ] as ColumnsType<DrillingData>
        : []),
    ], [isView, editingDid, drillTypeOptions, drillSizeOptions, barrelOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<DrillingData>
            columns={columns}
            pagination={false}
            dataSource={drillingData}
        />
    );
};

export default DrillingTable;